import { css, CSSInterpolation } from '@emotion/css';
import { useTheme } from '@emotion/react';
import { TableCell, TableSortLabel } from '@material-ui/core';
import { Padding } from '@material-ui/core/TableCell';
import cN from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { AutoSizer, Column, Index, Table } from 'react-virtualized';
import {
  getNextDirection,
  SortDirection,
  SortFn,
  useSort
} from '../../../hooks/useSort';
import { FullTheme } from '../../../themes';

export interface IColumn<RowData, SortKey, CellProps = undefined> {
  key: SortKey;
  head: (cellProps?: CellProps) => React.ReactNode;
  alternateHead?: (cellProps?: CellProps) => React.ReactNode;
  matchString?: (cellProps?: CellProps) => string;
  headInfo?: (cellProps?: CellProps) => string;
  align: 'left' | 'right';
  sortable?: boolean;
  defaultDirection?: SortDirection;
  cell: (rowData: RowData, rowProps: CellProps) => React.ReactNode;
  width: number;
  maxWidth?: number;
  flexGrow?: number;
  flexShrink?: number;
  showOverflowOnHover?: boolean;
  noWrap?: boolean;
}
export type VirtualizedSortableTableProps<
  RowData,
  SortKey,
  CellProps = undefined
> = {
  columns: IColumn<RowData, SortKey, CellProps>[];
  rows: RowData[];
  height: number; // or null to calculate it automatically?
  rowHeight?: number;
  margin: 'dense' | 'normal';
  initialSortColumn: IColumn<RowData, SortKey, CellProps>;
  initialSortDirection?: SortDirection;
  padding?: Padding;
  cellProps: CellProps;
  rowClassNameFn?: (item: RowData | null, theme: FullTheme) => CSSInterpolation; // null is the header row
  sortFn: (
    rows: RowData[],
    sortBy: SortKey,
    direction: SortDirection
  ) => RowData[];
  onSortChange?: (sortBy: SortKey, direction: SortDirection) => void;

  onRowClick?: (row: RowData) => void;

  visibleColumns?: Set<SortKey>;

  // Opt-in: size the table to the sum of its column widths and pin each column
  // to its defined width (no flexGrow) so it "hugs" its columns and left-aligns
  // instead of stretching to fill the container width.
  fitContent?: boolean;
};

const convertSortDirection = (
  dir: SortDirection
): 'ASC' | 'DESC' | undefined => {
  if (dir === 'asc') {
    return 'ASC';
  }
  if (dir === 'desc') {
    return 'DESC';
  }
  return undefined;
};

const SortableHead = <SortKey extends {}, CellProps = undefined>({
  column,
  currentDirection,
  currentSortBy,
  setSort,
  className,
  padding,
  cellProps
}: {
  column: IColumn<any, SortKey, CellProps>;
  setSort: SortFn<SortKey>;
  currentSortBy: SortKey;
  currentDirection: SortDirection;
  className: string;
  padding?: Padding;
  cellProps: CellProps;
}) => {
  const onSort = (cell: IColumn<any, SortKey, CellProps>) => {
    const nextDir =
      column.key === currentSortBy
        ? getNextDirection(currentDirection)
        : cell.defaultDirection || getNextDirection();
    setSort(cell.key, nextDir);
  };
  return (
    <TableCell
      component="div"
      align={column.align}
      variant="head"
      className={className}
      padding={padding}
    >
      {column.sortable ? (
        <TableSortLabel
          active={column.key === currentSortBy}
          direction={currentDirection}
          onClick={() => onSort(column)}
        >
          {column.head(cellProps)}
        </TableSortLabel>
      ) : (
        column.head(cellProps)
      )}
    </TableCell>
  );
};

const STANDARD_ROW_HEIGHT = 48;

const getStandardHeaderHeight = (padding?: Padding) => {
  return 56;
};

const getClassNames = (clickable: boolean, theme: FullTheme) => {
  const classNames = {
    table: css({ fontFamily: theme.typography.fontFamily }),
    cell: css({ flex: 1, height: 'inherit' }),
    cellContainer: css({ height: 'inherit' }),
    row: css({ cursor: clickable ? 'pointer' : 'default' }),
    flex: css({
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'border-box'
    })
  };

  return {
    table: cN(classNames.table),
    row: cN(classNames.row, classNames.flex),
    cell: cN(classNames.cell, classNames.flex),
    cellContainer: cN(classNames.cellContainer)
  };
};

const useRowSort = <T extends {}>(sortFn: () => T[], listeners: any[]) => {
  const [sortedRows, setSortedRows] = useState(sortFn());
  useEffect(() => {
    setSortedRows(sortFn());
    // eslint-disable-next-line
  }, listeners);

  return sortedRows;
};

export const VirtualizedSortableTable = <
  RowData extends {},
  SortKey extends string,
  CellProps = undefined
>({
  columns,
  rows,
  height,
  rowHeight = STANDARD_ROW_HEIGHT,
  initialSortColumn,
  initialSortDirection,
  sortFn,
  onSortChange,
  onRowClick,
  padding,
  cellProps,
  rowClassNameFn,
  visibleColumns,
  fitContent
}: VirtualizedSortableTableProps<RowData, SortKey, CellProps>) => {
  const theme = useTheme();
  const { sortBy, direction, setSort } = useSort(
    initialSortColumn.key,
    initialSortDirection || initialSortColumn.defaultDirection
  );
  const onSort = (k: SortKey, d: SortDirection) => {
    setSort(k, d);
    onSortChange && onSortChange(k, d);
  };
  const sortedRows = useRowSort(() => sortFn(rows, sortBy, direction), [
    rows,
    sortBy,
    direction
  ]);

  const isClickable = !!onRowClick;
  const classes = useMemo(() => getClassNames(isClickable, theme), [
    isClickable,
    theme
  ]);
  const headerHeight = getStandardHeaderHeight(padding);

  const rowClassName = rowClassNameFn
    ? ({ index }: Index) => {
        const item = sortedRows[index] || null;
        return cN(classes.row, css(rowClassNameFn(item, theme)));
      }
    : classes.row;

  const renderedColumns = visibleColumns
    ? columns.filter((c) => visibleColumns.has(c.key))
    : columns;

  // In fitContent mode the table width is the sum of the columns' own widths, so
  // the table hugs its content instead of stretching to the measured container
  // width. AutoSizer still measures (harmless); we just ignore its width.
  const fitContentWidth = renderedColumns.reduce((sum, c) => sum + c.width, 0);

  return (
    <AutoSizer disableHeight>
      {({ width }) => {
        return (
          <Table
            width={fitContent ? fitContentWidth : width}
            height={height}
            headerHeight={headerHeight}
            rowHeight={rowHeight}
            rowCount={sortedRows.length}
            rowGetter={({ index }) => sortedRows[index]}
            sortBy={sortBy}
            sortDirection={convertSortDirection(direction)}
            className={classes.table}
            rowClassName={rowClassName}
            onRowClick={({ index }) =>
              (onRowClick || (() => {}))(sortedRows[index])
            }
          >
            {renderedColumns.map((column) => (
              <Column
                key={column.key}
                className={classes.cellContainer}
                headerClassName={classes.cellContainer}
                dataKey={column.key}
                disableSort={!column.sortable}
                headerRenderer={() => (
                  <SortableHead<SortKey, CellProps>
                    column={column}
                    currentSortBy={sortBy}
                    currentDirection={direction}
                    setSort={onSort}
                    className={classes.cell}
                    padding={padding}
                    cellProps={cellProps}
                  />
                )}
                cellRenderer={({ rowData }) => (
                  <TableCell
                    component="div"
                    align={column.align}
                    variant="body"
                    className={classes.cell}
                  >
                    {column.cell(rowData, cellProps)}
                  </TableCell>
                )}
                width={column.width}
                maxWidth={column.maxWidth}
                flexGrow={fitContent ? 0 : column.flexGrow}
                flexShrink={fitContent ? 0 : column.flexShrink}
              />
            ))}
          </Table>
        );
      }}
    </AutoSizer>
  );
};
